import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  BookOpenCheck,
  CalendarDays,
  ChevronRight,
  FileText,
  GraduationCap,
  HardHat,
  MonitorCog,
  Scale,
  Users,
} from 'lucide-react';
import DashboardLayout from '../components/Layout/DashboardLayout';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import { InfoBadge } from '../components/UI/InfoBadge';
import { useTrainingMaterials } from '../hooks/useTrainingMaterialsData';

const CATEGORY = {
  compliance_ethics: {
    label: 'Compliance & Ethics',
    icon: Scale,
    dot: 'bg-violet-500',
    text: 'text-violet-700',
    soft: 'bg-violet-50',
  },
  workplace: {
    label: 'Workplace Awareness',
    icon: Users,
    dot: 'bg-teal-500',
    text: 'text-teal-700',
    soft: 'bg-teal-50',
  },
  safety: {
    label: 'Safety',
    icon: HardHat,
    dot: 'bg-amber-500',
    text: 'text-amber-700',
    soft: 'bg-amber-50',
  },
  information_security: {
    label: 'Information Security',
    icon: MonitorCog,
    dot: 'bg-sky-500',
    text: 'text-sky-700',
    soft: 'bg-sky-50',
  },
  general: {
    label: 'General',
    icon: GraduationCap,
    dot: 'bg-blue-500',
    text: 'text-blue-700',
    soft: 'bg-blue-50',
  },
};

const EMPTY_LIST = [];

const TrainingMaterialCard = ({ material }) => {
  const category = CATEGORY[material.category] || CATEGORY.general;
  const CategoryIcon = category.icon;

  return (
    <Link
      to={`/self-service/training-materials/${material.key}`}
      className="group card hover:shadow-lg transition-all duration-200 flex flex-col focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
    >
      <div className="card-content flex-1">
        <div className="flex items-start gap-3">
          <span className={`w-10 h-10 rounded-full ${category.soft} flex items-center justify-center flex-shrink-0`}>
            <CategoryIcon className={`w-5 h-5 ${category.text}`} aria-hidden="true" />
          </span>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <InfoBadge color="blue" label="Training material" size="xs" />
              {material.trainingPeriod && (
                <InfoBadge color="slate" label={material.trainingPeriod} size="xs" />
              )}
            </div>
            <h3 className="text-subsection-header leading-snug mt-2">{material.title}</h3>
            {material.subtitle && (
              <p className="text-xs font-medium text-gray-500 mt-1">{material.subtitle}</p>
            )}
            <p className="text-small mt-2 line-clamp-3">{material.summary}</p>
          </div>
          <ChevronRight
            className="w-5 h-5 text-gray-300 group-hover:text-gray-500 flex-shrink-0 mt-1"
            aria-hidden="true"
          />
        </div>
      </div>

      <div className="card-footer gap-3">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
          {material.sourcePageCount && (
            <span className="inline-flex items-center gap-1 whitespace-nowrap">
              <FileText className="w-3.5 h-3.5" aria-hidden="true" />
              {material.sourcePageCount} source pages
            </span>
          )}
          {material.sectionCount > 0 && (
            <span className="inline-flex items-center gap-1 whitespace-nowrap">
              <BookOpenCheck className="w-3.5 h-3.5" aria-hidden="true" />
              {material.sectionCount} sections
            </span>
          )}
          {material.reviewedOn && (
            <span className="inline-flex items-center gap-1 whitespace-nowrap">
              <CalendarDays className="w-3.5 h-3.5" aria-hidden="true" />
              Reviewed {material.reviewedOn}
            </span>
          )}
        </div>
      </div>
    </Link>
  );
};

const SelfServiceTrainingMaterials = () => {
  const { data, isLoading, isError } = useTrainingMaterials();
  const materials = data?.materials || EMPTY_LIST;
  const categories = data?.categories || EMPTY_LIST;

  const grouped = useMemo(
    () => categories
      .map((category) => ({
        ...category,
        items: materials.filter((material) => material.category === category.id),
      }))
      .filter((category) => category.items.length > 0),
    [categories, materials],
  );

  return (
    <DashboardLayout>
      <div className="space-y-6 md:space-y-8 bg-gray-50 min-h-screen pb-12">
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          <div className="p-4 sm:p-6 lg:p-8">
            <div className="flex items-start gap-4">
              <Link
                to="/self-service"
                aria-label="Back to Self-Service"
                title="Back to Self-Service"
                className="icon-button flex-shrink-0"
              >
                <ArrowLeft className="w-5 h-5" aria-hidden="true" />
              </Link>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2.5 min-w-0">
                  <h1 className="text-section-header truncate min-w-0">Training Materials</h1>
                  <InfoBadge
                    color="slate"
                    label="Internal"
                    title="Internal — for company use only"
                    className="flex-shrink-0"
                  />
                </div>
                <p className="text-body mt-0.5">
                  Read company training and awareness resources at your own pace.
                </p>
              </div>
            </div>

            <div className="mt-5 flex items-start gap-3 rounded-xl border border-blue-100 bg-blue-50 p-4">
              <BookOpenCheck className="w-5 h-5 text-blue-700 flex-shrink-0 mt-0.5" aria-hidden="true" />
              <div>
                <p className="text-sm font-semibold text-blue-900">Reference library</p>
                <p className="text-sm leading-relaxed text-blue-800 mt-0.5">
                  These resources do not include a quiz or acknowledgement. Formal company policies remain in Company Policies.
                </p>
              </div>
            </div>
          </div>
        </div>

        {isLoading ? (
          <div className="card">
            <div className="card-content py-16"><LoadingSpinner /></div>
          </div>
        ) : isError ? (
          <div className="card">
            <div className="card-content text-center py-12">
              <FileText className="w-8 h-8 text-red-300 mx-auto mb-3" aria-hidden="true" />
              <h2 className="text-subsection-header">Training materials could not be loaded</h2>
              <p className="text-body mt-1">Please refresh the page or try again later.</p>
            </div>
          </div>
        ) : grouped.length === 0 ? (
          <div className="card">
            <div className="card-content text-center py-12">
              <BookOpenCheck className="w-8 h-8 text-gray-300 mx-auto mb-3" aria-hidden="true" />
              <h2 className="text-subsection-header">No training materials published yet</h2>
              <p className="text-body mt-1">Published employee resources will appear here.</p>
            </div>
          </div>
        ) : (
          <div className="space-y-8">
            {grouped.map((group) => {
              const category = CATEGORY[group.id] || CATEGORY.general;
              return (
                <section key={group.id} className="space-y-4">
                  <div className="flex items-center gap-2 px-1">
                    <span className={`w-1.5 h-1.5 rounded-full ${category.dot}`} aria-hidden="true" />
                    <h2 className={`text-xs font-semibold uppercase tracking-wider ${category.text}`}>
                      {category.label}
                    </h2>
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {group.items.map((material) => (
                      <TrainingMaterialCard key={material.key} material={material} />
                    ))}
                  </div>
                </section>
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default SelfServiceTrainingMaterials;

